import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { invitationService, InvitationSummary } from '@/services/invitationService'
import { useAuth } from '@/hooks/useAuth'

/**
 * InvitationClaimPage — M5 recipient claim entry point.
 *
 * Route: /invitations/claim/:token
 *
 * States:
 *  - loading: fetching invitation by token
 *  - error:   token invalid / expired / not found
 *  - not_logged_in: guide to register / login with invitation context
 *  - email_mismatch: logged-in user email != invitation email
 *  - claimable: user email matches -> auto-call claim on mount
 *  - claimed: success
 */
export const InvitationClaimPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const [invitation, setInvitation] = useState<InvitationSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [claiming, setClaiming] = useState(false)
  const [claimed, setClaimed] = useState(false)

  useEffect(() => {
    if (!token) return
    void (async () => {
      try {
        const inv = await invitationService.getByToken(token)
        setInvitation(inv)
      } catch (e) {
        setError((e as Error).message || 'Undangan tidak ditemukan')
      } finally {
        setLoading(false)
      }
    })()
  }, [token])

  useEffect(() => {
    // Auto-claim when user is logged in and email matches.
    if (!invitation || !isAuthenticated || !user) return
    if (user.email?.toLowerCase() !== invitation.invitedEmail.toLowerCase()) return
    if (claimed || claiming) return
    setClaiming(true)
    void (async () => {
      try {
        await invitationService.claim(invitation.id)
        setClaimed(true)
      } catch (e) {
        setError((e as Error).message || 'Gagal klaim')
      } finally {
        setClaiming(false)
      }
    })()
  }, [invitation, isAuthenticated, user, claimed, claiming])

  if (loading) {
    return <div className="p-6" data-testid="invitation-loading">Memuat undangan...</div>
  }
  if (error || !invitation) {
    return (
      <div className="p-6" data-testid="invitation-error">
        <h1 className="text-xl font-semibold mb-2">Undangan tidak valid</h1>
        <p className="text-sm text-gray-600">{error || 'Token tidak dikenali'}</p>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="p-6 space-y-3" data-testid="invitation-not-logged-in">
        <h1 className="text-xl font-semibold">Anda perlu login</h1>
        <p className="text-sm text-gray-600">
          Undangan ini ditujukan untuk <strong>{invitation.invitedEmail}</strong>. Silakan
          login atau daftar dengan email tersebut untuk mengklaim tiket.
        </p>
        <div className="flex gap-2">
          <button
            onClick={() =>
              navigate(
                `/login?email=${encodeURIComponent(invitation.invitedEmail)}&redirect=${encodeURIComponent(
                  `/invitations/claim/${token}`,
                )}`,
              )
            }
            className="px-4 py-2 bg-primary text-white rounded"
          >
            Login
          </button>
          <button
            onClick={() =>
              navigate(
                `/register?email=${encodeURIComponent(invitation.invitedEmail)}&invitation=${encodeURIComponent(
                  token || '',
                )}`,
              )
            }
            className="px-4 py-2 border rounded"
          >
            Daftar
          </button>
        </div>
      </div>
    )
  }

  if (user && user.email?.toLowerCase() !== invitation.invitedEmail.toLowerCase()) {
    return (
      <div className="p-6" data-testid="invitation-mismatch">
        <h1 className="text-xl font-semibold mb-2">Email tidak sesuai</h1>
        <p className="text-sm text-gray-600">
          Undangan ini untuk <strong>{invitation.invitedEmail}</strong>, tapi Anda login dengan{' '}
          <strong>{user.email}</strong>. Silakan logout dan login ulang dengan email tujuan.
        </p>
      </div>
    )
  }

  if (claimed) {
    return (
      <div className="p-6 space-y-3" data-testid="invitation-claimed">
        <h1 className="text-xl font-semibold">Tiket berhasil diklaim</h1>
        <p className="text-sm text-gray-600">Anda sekarang terdaftar di kursus ini.</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 bg-primary text-white rounded"
        >
          Ke Dashboard
        </button>
      </div>
    )
  }

  return (
    <div className="p-6" data-testid="invitation-claiming">
      <p>Mengklaim tiket...</p>
    </div>
  )
}

export default InvitationClaimPage
